import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Clock, Mail, XCircle } from 'lucide-react';

import { apiService } from '../../services/apiService';
import {
  SalesAgentApprovalRequest,
  SalesAgentEmail,
} from '../../services/salesAgentTypes';

import { dateTime } from './format';

const MAX_NOTE_LENGTH = 1000;

interface ApprovalPanelProps {
  email: SalesAgentEmail;
  /** Called once the decision is recorded (or refused), to reload the page's data */
  onDecided: () => Promise<void>;
}

/**
 * An RFQ proposal's approval, under its row. While it waits: where the proposal is, and Approve (with the number of
 * the Epicor quote the rep created) or Reject (with an optional note). Once decided: who decided and when.
 */
export const ApprovalPanel: React.FC<ApprovalPanelProps> = ({
  email,
  onDecided,
}) => {
  const { t, i18n } = useTranslation();
  const [quoteNum, setQuoteNum] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const quoteId = useId();
  const noteId = useId();

  const at = dateTime(email.approval_at, i18n.language);
  const by = email.approval_by ?? '—';
  const validQuoteNum = /^\d+$/.test(quoteNum.trim()) && Number(quoteNum) > 0;

  const decide = async (decision: SalesAgentApprovalRequest) => {
    setSaving(true);
    setError(null);
    try {
      await apiService.decideSalesAgentApproval(email.emailKey, decision);
      await onDecided();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('salesAgent.approval.error')
      );
      // A 409 means someone else decided, or the workflow stopped waiting: show the row as it is now
      await onDecided();
    } finally {
      setSaving(false);
    }
  };

  const savedNote = email.approval_note ? (
    <p className="mt-1 text-sm text-gray-700 whitespace-pre-line">
      {email.approval_note}
    </p>
  ) : null;

  if (email.approval_status === 'approved') {
    return (
      <div className="flex items-start gap-2 text-sm text-green-800">
        <CheckCircle className="w-4 h-4 mt-0.5" aria-hidden="true" />
        <div>
          <p>
            {t('salesAgent.approval.approved', { by, at })}
            {email.quote_num !== undefined &&
              ` · ${t('salesAgent.approval.quote', { quoteNum: email.quote_num })}`}
          </p>
          {savedNote}
        </div>
      </div>
    );
  }

  if (email.approval_status === 'rejected') {
    return (
      <div className="flex items-start gap-2 text-sm text-gray-800">
        <XCircle className="w-4 h-4 mt-0.5 text-red-600" aria-hidden="true" />
        <div>
          <p>{t('salesAgent.approval.rejected', { by, at })}</p>
          {savedNote}
        </div>
      </div>
    );
  }

  if (email.approval_status === 'expired') {
    return (
      <div className="flex items-start gap-2 text-sm text-gray-600">
        <Clock className="w-4 h-4 mt-0.5" aria-hidden="true" />
        <p>{t('salesAgent.approval.expired')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="flex items-center gap-2 text-sm text-gray-800">
        <Mail className="w-4 h-4 text-blue-600" aria-hidden="true" />
        <span className="font-medium">{t('salesAgent.approval.waiting')}</span>
        <span className="text-gray-600">
          {t('salesAgent.approval.inDrafts')}
          {email.approval_requested_at &&
            ` · ${t('salesAgent.approval.requested', {
              at: dateTime(email.approval_requested_at, i18n.language),
            })}`}
        </span>
      </p>
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div>
          <label
            htmlFor={quoteId}
            className="block text-xs font-medium text-gray-700"
          >
            {t('salesAgent.approval.quoteNum')}
          </label>
          <input
            id={quoteId}
            type="text"
            inputMode="numeric"
            value={quoteNum}
            onChange={(e) => setQuoteNum(e.target.value)}
            disabled={saving}
            aria-describedby={`${quoteId}-help`}
            className="mt-1 w-40 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p id={`${quoteId}-help`} className="mt-1 text-xs text-gray-500">
            {t('salesAgent.approval.quoteNumHelp')}
          </p>
        </div>
        <div className="flex-1">
          <label
            htmlFor={noteId}
            className="block text-xs font-medium text-gray-700"
          >
            {t('salesAgent.approval.note')}
          </label>
          <textarea
            id={noteId}
            rows={2}
            maxLength={MAX_NOTE_LENGTH}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={saving}
            className="mt-1 w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={saving || !validQuoteNum}
            onClick={() =>
              decide({ approved: true, quoteNum: Number(quoteNum), note })
            }
            className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {t('salesAgent.approval.approve')}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => decide({ approved: false, note })}
            className="px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50"
          >
            {t('salesAgent.approval.reject')}
          </button>
        </div>
      </div>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
    </div>
  );
};
